import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "searchResults", "customList"]

  connect() {
    this._debounceTimer = null
  }

  // --- Search ---

  preventSubmit(event) {
    event.preventDefault()
  }

  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._doSearch(), 200)
  }

  async _doSearch() {
    const q = this.searchInputTarget.value.trim()
    const res = await fetch(`/animations/search?q=${encodeURIComponent(q)}`)
    if (!res.ok) return

    const animations = await res.json()
    const assigned = this._assignedIds()

    this.searchResultsTarget.innerHTML = animations
      .filter(a => !assigned.has(a.id))
      .map(a => `
        <button type="button"
                data-action="click->animation-manager#addCustom"
                data-anim-id="${a.id}"
                data-anim-name="${a.name}"
                data-anim-loop="${a.loop_mode}"
                class="flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs hover:bg-accent/10 rounded transition-colors cursor-pointer"
                style="font-family: var(--font-mono);">
          <span class="text-fg">${a.name}</span>
          <span class="text-fg-muted/50 text-[10px]">${a.loop_mode}</span>
        </button>
      `).join('') || '<p class="text-[10px] text-fg-muted px-2 py-1.5">No animations found</p>'

    this.searchResultsTarget.classList.remove("hidden")
  }

  hideResults() {
    setTimeout(() => {
      this.searchResultsTarget.classList.add("hidden")
    }, 200)
  }

  // --- System state assignment ---

  assignSystemState(event) {
    const state = event.currentTarget.dataset.state
    this._openSearchForState(state, event.currentTarget)
  }

  async _openSearchForState(state, btn) {
    const res = await fetch(`/animations/search?q=`)
    if (!res.ok) return
    const animations = await res.json()

    const dropdown = document.createElement("div")
    dropdown.className = "absolute z-50 mt-1 w-64 max-h-48 overflow-y-auto bg-white border border-border-lt rounded-lg shadow-lg"
    dropdown.innerHTML = `
      <input type="text" placeholder="Search..." class="w-full px-2 py-1.5 text-xs border-b border-border-lt outline-none" style="font-family: var(--font-mono);" data-state-search>
      <div data-state-results>
        ${animations.map(a => `
          <button type="button" data-anim-id="${a.id}" data-anim-name="${a.name}" data-anim-loop="${a.loop_mode}" data-anim-url="${a.url || ''}"
                  class="state-result flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs hover:bg-accent/10 transition-colors cursor-pointer"
                  style="font-family: var(--font-mono);">
            <span>${a.name}</span>
            <span class="text-fg-muted/50 text-[10px]">${a.loop_mode}</span>
          </button>
        `).join('')}
      </div>
    `

    btn.parentElement.style.position = "relative"
    btn.parentElement.appendChild(dropdown)

    const searchInput = dropdown.querySelector("[data-state-search]")
    searchInput.focus()

    searchInput.addEventListener("input", async () => {
      const q = searchInput.value.trim()
      const r = await fetch(`/animations/search?q=${encodeURIComponent(q)}`)
      if (!r.ok) return
      const results = await r.json()
      const container = dropdown.querySelector("[data-state-results]")
      container.innerHTML = results.map(a => `
        <button type="button" data-anim-id="${a.id}" data-anim-name="${a.name}" data-anim-loop="${a.loop_mode}" data-anim-url="${a.url || ''}"
                class="state-result flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs hover:bg-accent/10 transition-colors cursor-pointer"
                style="font-family: var(--font-mono);">
          <span>${a.name}</span>
          <span class="text-fg-muted/50 text-[10px]">${a.loop_mode}</span>
        </button>
      `).join('') || '<p class="text-[10px] text-fg-muted px-2 py-1.5">No results</p>'
    })

    dropdown.addEventListener("click", (e) => {
      const resultBtn = e.target.closest(".state-result")
      if (!resultBtn) return
      this._setSystemState(state, resultBtn.dataset.animId, resultBtn.dataset.animName, resultBtn.dataset.animLoop, resultBtn.dataset.animUrl)
      dropdown.remove()
    })

    const closeOnOutside = (e) => {
      if (!dropdown.contains(e.target) && e.target !== btn) {
        dropdown.remove()
        document.removeEventListener("click", closeOnOutside)
      }
    }
    setTimeout(() => document.addEventListener("click", closeOnOutside), 0)
  }

  _setSystemState(state, animId, animName, loopMode, animUrl) {
    const slot = this.element.querySelector(`[data-system-state="${state}"]`)
    if (!slot) return

    slot.innerHTML = `
      <span class="text-xs text-fg px-2 py-1.5 bg-accent/10 rounded" style="font-family: var(--font-mono);">${animName}</span>
      <span class="text-[10px] text-fg-muted" style="font-family: var(--font-mono);">${loopMode}</span>
      <input type="hidden" name="persona_animations[system_${state}][animation_id]" value="${animId}">
      <input type="hidden" name="persona_animations[system_${state}][state]" value="${state}">
      <button type="button" data-action="click->persona-preview#playAnimation"
              data-animation-name="${animName}"
              data-animation-url="${animUrl || ''}"
              class="text-fg-muted/40 hover:text-accent transition-colors ml-auto" title="Preview">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
        </svg>
      </button>
      <button type="button" data-action="click->animation-manager#removeSystemState" data-state="${state}"
              class="text-fg-muted/40 hover:text-red-500 transition-colors" title="Remove">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
          <path fill-rule="evenodd" d="M4.72 3.97a.75.75 0 011.06 0L10 8.19l4.22-4.22a.75.75 0 111.06 1.06L11.06 9.25l4.22 4.22a.75.75 0 11-1.06 1.06L10 10.31l-4.22 4.22a.75.75 0 01-1.06-1.06l4.22-4.22-4.22-4.22a.75.75 0 010-1.06z" clip-rule="evenodd" />
        </svg>
      </button>
    `
  }

  removeSystemState(event) {
    const state = event.currentTarget.dataset.state
    const slot = this.element.querySelector(`[data-system-state="${state}"]`)
    if (!slot) return

    slot.innerHTML = `
      <span class="text-xs text-fg-muted/50" style="font-family: var(--font-mono);">none assigned</span>
      <button type="button" data-action="click->animation-manager#assignSystemState" data-state="${state}"
              class="text-xs text-accent hover:text-accent-dk transition-colors ml-auto" style="font-family: var(--font-mono);">
        + assign
      </button>
    `
  }

  // --- Custom animations ---

  addCustom(event) {
    const id = event.currentTarget.dataset.animId
    const name = event.currentTarget.dataset.animName
    const loopMode = event.currentTarget.dataset.animLoop
    const url = event.currentTarget.dataset.animUrl

    const row = document.createElement("div")
    row.className = "flex items-center gap-2"
    row.dataset.animationEntry = id
    row.innerHTML = `
      <span class="text-xs text-fg px-2 py-1.5 bg-accent/10 rounded min-w-[120px] shrink-0" style="font-family: var(--font-mono);">${name}</span>
      <span class="text-[10px] text-fg-muted shrink-0 w-20" style="font-family: var(--font-mono);">${loopMode}</span>
      <input type="text" name="persona_animations[custom_${id}][trigger]"
             placeholder="When to use this animation..."
             class="input-futuristic text-xs px-2 py-1.5 rounded flex-1"
             style="font-family: var(--font-mono);">
      <input type="hidden" name="persona_animations[custom_${id}][animation_id]" value="${id}">
      <input type="hidden" name="persona_animations[custom_${id}][state]" value="custom">
      <button type="button" data-action="click->persona-preview#playAnimation"
              data-animation-name="${name}"
              data-animation-url="${url || ''}"
              class="text-fg-muted/40 hover:text-accent transition-colors shrink-0" title="Preview">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
        </svg>
      </button>
      <button type="button" data-action="click->animation-manager#removeCustom"
              class="text-fg-muted/40 hover:text-red-500 transition-colors shrink-0" title="Remove">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
          <path fill-rule="evenodd" d="M4.72 3.97a.75.75 0 011.06 0L10 8.19l4.22-4.22a.75.75 0 111.06 1.06L11.06 9.25l4.22 4.22a.75.75 0 11-1.06 1.06L10 10.31l-4.22 4.22a.75.75 0 01-1.06-1.06l4.22-4.22-4.22-4.22a.75.75 0 010-1.06z" clip-rule="evenodd" />
        </svg>
      </button>
    `

    this.customListTarget.appendChild(row)
    this.searchInputTarget.value = ""
    this.searchResultsTarget.classList.add("hidden")
  }

  removeCustom(event) {
    event.currentTarget.closest("[data-animation-entry]").remove()
  }

  // --- Helpers ---

  _assignedIds() {
    const ids = new Set()
    this.element.querySelectorAll("input[name*='[animation_id]']").forEach(input => {
      ids.add(parseInt(input.value))
    })
    return ids
  }
}
